import React from 'react';
import { Info, Droplet, Filter, ShowerHead, Zap, AlertTriangle, ArrowRight, Thermometer } from 'lucide-react';

/**
 * Компонент с информацией о бассейнах и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о бассейнах: устройство, типы, обслуживание
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Droplet className="w-5 h-5 text-blue-500" />
          Типы бассейнов, популярные в России
        </h3>
        
        <p>
          Несмотря на относительно короткий летний сезон, бассейны становятся всё более популярными в России как для частного, так и для коммерческого использования. Выбор типа бассейна зависит от климатических условий, бюджета и предназначения.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
            <h4 className="text-lg font-medium text-blue-800 dark:text-blue-300 mb-2">Каркасные бассейны</h4>
            <p className="text-blue-700 dark:text-blue-400 text-sm">
              Самый доступный вариант для дачи и загородного дома. Легко устанавливаются и демонтируются, но имеют ограниченный срок службы (3-5 лет). Объем воды обычно 5-15 м³.
            </p>
          </div>
          
          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
            <h4 className="text-lg font-medium text-indigo-800 dark:text-indigo-300 mb-2">Стационарные бетонные</h4>
            <p className="text-indigo-700 dark:text-indigo-400 text-sm">
              Наиболее долговечный вариант (50+ лет) с возможностью любой формы. Популярны для постоянных резиденций. Объем воды обычно 20-60 м³ для частных и до 1000+ м³ для общественных.
            </p>
          </div>
          
          <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-100 dark:border-cyan-800">
            <h4 className="text-lg font-medium text-cyan-800 dark:text-cyan-300 mb-2">Композитные бассейны</h4>
            <p className="text-cyan-700 dark:text-cyan-400 text-sm">
              Чаши из стекловолокна или композитных материалов. Быстрая установка (3-7 дней), срок службы 25+ лет. Становятся популярными в Подмосковье и южных регионах. Объем 15-35 м³.
            </p>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
            <h4 className="text-lg font-medium text-purple-800 dark:text-purple-300 mb-2">Крытые бассейны</h4>
            <p className="text-purple-700 dark:text-purple-400 text-sm">
              Оптимальны для российского климата с длительными холодными периодами. Могут использоваться круглый год. Требуют дополнительных затрат на вентиляцию и контроль влажности.
            </p>
          </div>
        </div>

        <h3 className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-500" />
          Особенности обслуживания в российских условиях
        </h3>
        
        <p>
          Использование и обслуживание бассейнов в России имеет ряд особенностей, связанных с климатом и доступностью материалов.
        </p>
        
        <ul>
          <li><strong>Сезонность использования</strong> – большинство открытых бассейнов эксплуатируются с мая/июня по август/сентябрь в центральной полосе России, дольше – в южных регионах</li>
          <li><strong>Консервация на зиму</strong> – для открытых бассейнов требуется специальная процедура консервации с опорожнением или частичным сливом воды</li>
          <li><strong>Повышенный расход реагентов</strong> – из-за перепадов температур и периодов простоя может потребоваться больше химии для обеззараживания</li>
          <li><strong>Система подогрева</strong> – для комфортного использования в большинстве регионов необходима система подогрева воды, что повышает эксплуатационные расходы</li>
          <li><strong>Закрытие от солнца</strong> – рост водорослей в жаркие летние месяцы требует использования альгицидов и укрытий для бассейна</li>
        </ul>
        
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Особенности климатических зон России
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>В северных регионах бассейны рекомендуется строить в закрытых помещениях или под навесами с подогревом</li>
            <li>В средней полосе оптимальны системы с подогревом и укрытиями для продления сезона</li>
            <li>В южных регионах (Краснодарский край, Крым) важны системы охлаждения и защита от солнца</li>
            <li>В континентальных регионах (Сибирь) требуется усиленная теплоизоляция для защиты от перепадов температур</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <ShowerHead className="w-5 h-5 text-blue-500" />
          Система фильтрации и очистки воды
        </h3>
        
        <p>
          Правильно подобранная система фильтрации и очистки воды – залог комфортного и безопасного использования бассейна. В России наиболее распространены следующие системы:
        </p>
        
        <div className="overflow-x-auto my-4">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Тип системы</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Принцип работы</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Распространенность</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Средняя стоимость</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-300 dark:divide-gray-600">
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium">Хлорирование</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Дезинфекция с помощью хлорсодержащих препаратов</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Очень высокая</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">15-30 тыс. ₽ (оборудование)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium">Солевой электролиз</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Генерация хлора из поваренной соли</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Средняя</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">60-120 тыс. ₽</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium">Озонирование</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Обеззараживание с помощью озона</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Низкая</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">100-200 тыс. ₽</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium">УФ-обработка</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Уничтожение микробов ультрафиолетом</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">Средняя</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">40-80 тыс. ₽</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <p>
          В российских условиях наиболее распространена комбинированная система: механическая фильтрация (песочный или картриджный фильтр) + химическая обработка воды (хлор или бром). Для крытых бассейнов предпочтительны системы с пониженным содержанием хлора (озон + УФ).
        </p>

        <h3 className="flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-blue-500" />
          Подогрев бассейна и энергоэффективность
        </h3>
        
        <p>
          В российском климате подогрев бассейна - не роскошь, а необходимость для комфортного использования. Даже в южных регионах температура воды без подогрева может быть ниже комфортной (24-28°C).
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">Электрические нагреватели</h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Быстрый нагрев, легкая установка. Высокие расходы на электричество.
                <br/>
                <span className="block mt-2 font-medium text-gray-700 dark:text-gray-300">Стоимость: 40 000 - 100 000 ₽</span>
              </p>
            </div>
          </div>
          
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">Тепловые насосы</h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Энергоэффективны, низкие эксплуатационные расходы. Высокая стоимость оборудования.
                <br/>
                <span className="block mt-2 font-medium text-gray-700 dark:text-gray-300">Стоимость: 150 000 - 400 000 ₽</span>
              </p>
            </div>
          </div>
          
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">Солнечные нагреватели</h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Экологичны, минимальные эксплуатационные расходы. Эффективны только в южных регионах России.
                <br/>
                <span className="block mt-2 font-medium text-gray-700 dark:text-gray-300">Стоимость: 60 000 - 200 000 ₽</span>
              </p>
            </div>
          </div>
          
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">Газовые нагреватели</h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Быстрый нагрев, средняя стоимость эксплуатации. Требуют подключения к газовой сети.
                <br/>
                <span className="block mt-2 font-medium text-gray-700 dark:text-gray-300">Стоимость: 80 000 - 250 000 ₽</span>
              </p>
            </div>
          </div>
        </div>
        
        <p>
          В средней полосе России тепловые насосы окупаются за 3-5 лет при интенсивном использовании бассейна. 
          В южных регионах оптимальны солнечные нагреватели. При наличии газопровода наиболее экономичным решением будут газовые нагреватели.
        </p>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-blue-500" />
          Советы по эксплуатации в российских условиях
        </h3>
        
        <div className="space-y-3 my-4">
          <div className="flex gap-2">
            <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Продление сезона использования</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Используйте солнечные покрытия (Solar Cover) для удержания тепла в межсезонье. 
                Они повышают температуру воды на 4-8°C и снижают испарение на 70%.
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Консервация на зиму</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                В регионах с отрицательными зимними температурами необходима консервация бассейна. 
                Не сливайте воду полностью из бетонного бассейна, это может привести к повреждению чаши. 
                Уровень воды опускают на 30 см ниже скиммеров и добавляют специальные реагенты.
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Экономия на обслуживании</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Установите автоматические дозаторы хлора и pH-корректоров. 
                Они позволят снизить расход химии на 20-30% за счет точной дозировки. 
                Регулярно проверяйте параметры воды с помощью тестеров.
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Правильный режим фильтрации</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                В летний период в России оптимальное время работы фильтрации - 8-12 часов в сутки. 
                Используйте таймеры для автоматического включения насоса ночью, когда электричество дешевле (при двухтарифном учете).
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Уход за водой в жару</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                В периоды летней жары (30°C и выше), характерной для многих регионов России, 
                увеличивайте дозировку альгицида и время работы фильтрации. 
                Проверяйте уровень хлора ежедневно, так как на солнце он быстро разрушается.
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Info className="w-4 h-4" />
            Нормативные требования в России
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm">
            Строительство и эксплуатация частных бассейнов в России регулируются СП 31-113-2004 и СанПиН 2.1.2.1188-03. 
            Для общественных бассейнов действуют более строгие требования по качеству воды и системам циркуляции. 
            Частные бассейны не подлежат обязательной сертификации, но рекомендуется придерживаться норм по гигиеническим показателям воды.
          </p>
        </div>

        <h3>Заключение</h3>
        <p>
          Правильный расчет объема бассейна - важный шаг для подбора оборудования и оценки эксплуатационных расходов. 
          Учитывайте особенности российского климата и доступность материалов при планировании строительства и обслуживания. 
          Современные технологии позволяют эффективно использовать бассейны даже в регионах с коротким летним сезоном, 
          а правильная консервация обеспечит долгий срок службы.
        </p>
        
        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам точно рассчитать объем бассейна любой формы и получить рекомендации по выбору оборудования, 
          подходящего для российских условий!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;